import { ChatComposer, ChatToolButton } from '@eriko/ui/src/components/ui/chat';
import { AlertCircle, FileText, Paperclip, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FileReference, UploadingFile } from '../../../hooks/useFileUpload';
import { useChatStore } from '../../../stores/chatStore';

interface ChatInputProps {
  onSend: (message: string, files?: FileReference[]) => void;
  disabled?: boolean;
  disabledReason?: string;
  files: UploadingFile[];
  pendingFiles: UploadingFile[];
  isUploading: boolean;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  openFilePicker: () => void;
}

function FileChip({ file, onRemove }: { file: UploadingFile; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 bg-surface-100 border border-surface-200 rounded-lg text-xs">
      {file.status === 'uploading' && (
        <div className="w-3.5 h-3.5 border-2 border-surface-500 border-t-transparent rounded-full animate-spin shrink-0" />
      )}
      {file.status === 'done' && <FileText className="w-3.5 h-3.5 text-pos shrink-0" />}
      {file.status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-neg shrink-0" />}
      <span className="truncate max-w-37.5 font-medium" title={file.error ?? file.fileName}>
        {file.fileName}
      </span>
      {file.status === 'uploading' && (
        <span className="font-mono text-[10px] text-surface-500 shrink-0">{file.progress}%</span>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="text-surface-400 hover:text-surface-700 shrink-0 cursor-pointer"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

export function ChatInput({
  onSend,
  disabled,
  disabledReason,
  files,
  pendingFiles,
  isUploading,
  removeFile,
  clearFiles,
  openFilePicker,
}: ChatInputProps) {
  const { t } = useTranslation('chat');
  const pendingAnswers = useChatStore((s) => s.pendingAnswers);
  const pendingAnswerCount = pendingAnswers ? Object.keys(pendingAnswers.answers).length : 0;

  const handleSend = (message: string) => {
    if (isUploading) return;

    const fileRefs: FileReference[] | undefined =
      pendingFiles.length > 0
        ? pendingFiles.map((f) => ({
            uploadId: f.uploadId!,
            documentId: f.documentId,
            fileKey: f.fileKey,
            fileName: f.fileName,
            fileType: f.fileType,
            fileUrl: f.fileUrl!,
          }))
        : undefined;

    onSend(message, fileRefs);
    clearFiles();
  };

  if (disabledReason) {
    return (
      <div className="px-7 py-4 border-t border-surface-200 bg-transparent">
        <p className="text-xs text-surface-500 italic">{disabledReason}</p>
      </div>
    );
  }

  return (
    <ChatComposer
      onSend={handleSend}
      disabled={disabled || isUploading}
      allowEmptySend={pendingAnswerCount > 0}
      placeholder={
        pendingAnswerCount > 0
          ? `${pendingAnswerCount} answer${pendingAnswerCount === 1 ? '' : 's'} staged — hit Send, or add a note…`
          : t('input.placeholder')
      }
      topContent={
        files.length > 0 ? (
          <div className="flex flex-wrap gap-2 px-3 pt-2 pb-1">
            {files.map((file) => (
              <FileChip key={file.id} file={file} onRemove={() => removeFile(file.id)} />
            ))}
          </div>
        ) : undefined
      }
      toolButtons={
        <ChatToolButton title={t('input.attach')} onClick={openFilePicker}>
          <Paperclip className="w-4 h-4" />
        </ChatToolButton>
      }
    />
  );
}
